import { SimpleAmount } from '@mews-commander/data-access';
import { LocalizedCurrencyValue, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner } from '@optimus-web/core';
import { SmsGuideLink } from '../../SmsGuideLink';
import { AvailableBudgetBannerState, getAvailableBudgetBannerState } from './bannerState';

interface AvailableBudgetBannerProps {
    fee: SimpleAmount;
    maxMonthlyBudget: SimpleAmount;
    remainingBudget: SimpleAmount;
    rolloverFromPriorMonths: SimpleAmount;
    startingBudget: SimpleAmount;
    startingBudgetForNextMonth: SimpleAmount | null | undefined;
    toppedUpFee: SimpleAmount;
}

export const AvailableBudgetBanner = ({
    fee,
    maxMonthlyBudget,
    remainingBudget,
    rolloverFromPriorMonths,
    startingBudget,
    startingBudgetForNextMonth,
    toppedUpFee,
}: AvailableBudgetBannerProps) => {
    const bannerState = getAvailableBudgetBannerState({
        fee,
        remainingBudget,
        rolloverFromPriorMonths,
        startingBudget,
        startingBudgetForNextMonth,
        toppedUpFee,
        maxMonthlyBudget,
    });

    switch (bannerState.type) {
        case AvailableBudgetBannerState.NegativeBalance: {
            return (
                <Banner
                    variant="error"
                    primaryText={
                        <LocalizedText
                            textKey={TextKey.TextMessagingConfigurationBannerInfoBudgetOverspendCurrentMonth}
                            substitutes={{
                                spentAmount: (
                                    <LocalizedCurrencyValue
                                        value={{
                                            value: -remainingBudget.value,
                                            currency: remainingBudget.currencyCode,
                                        }}
                                    />
                                ),
                                startAmount: (
                                    <LocalizedCurrencyValue
                                        value={{
                                            value: bannerState.startingBudgetForNextMonth.value,
                                            currency: bannerState.startingBudgetForNextMonth.currencyCode,
                                        }}
                                    />
                                ),
                            }}
                        />
                    }
                />
            );
        }
        case AvailableBudgetBannerState.MonthlyBudgetToppedUp: {
            return (
                <Banner
                    variant="success"
                    primaryText={
                        <LocalizedText
                            textKey={TextKey.TextMessagingConfigurationBannerSuccessToppedUp}
                            substitutes={{
                                helpArticle: (text) => <SmsGuideLink text={text} />,
                                topUpAmount: (
                                    <LocalizedCurrencyValue
                                        value={{
                                            value: toppedUpFee.value,
                                            currency: toppedUpFee.currencyCode,
                                        }}
                                    />
                                ),
                                maximumBudget: (
                                    <LocalizedCurrencyValue
                                        value={{
                                            value: maxMonthlyBudget.value,
                                            currency: maxMonthlyBudget.currencyCode,
                                        }}
                                    />
                                ),
                            }}
                        />
                    }
                />
            );
        }
        case AvailableBudgetBannerState.BalanceCarriedOver: {
            return (
                <Banner
                    variant="info"
                    primaryText={
                        <LocalizedText
                            textKey={TextKey.TextMessagingConfigurationBannerInfoBudgetOverspendLastMonth}
                            substitutes={{
                                spentAmount: (
                                    <LocalizedCurrencyValue
                                        value={{
                                            value: -rolloverFromPriorMonths.value,
                                            currency: rolloverFromPriorMonths.currencyCode,
                                        }}
                                    />
                                ),
                                startAmount: (
                                    <LocalizedCurrencyValue
                                        value={{
                                            value: startingBudget.value,
                                            currency: startingBudget.currencyCode,
                                        }}
                                    />
                                ),
                                helpArticle: (text) => <SmsGuideLink text={text} />,
                            }}
                        />
                    }
                />
            );
        }
        case AvailableBudgetBannerState.RegularBalance:
        default: {
            return null;
        }
    }
};
